import { useEffect, useMemo, useState } from "react";
import { getJson, nf, ago, card, control, entityPath, KevBadge, relationshipLine, CATEGORY_BY_SLUG } from "./shared";
import { StixExportLink, StixTypeTag, queryOf } from "./Stix";

// The Vulnerabilities list: every CVE the Knowledge Base knows (CISA KEV, ATT&CK,
// detection rules, recent critical CVEs from the NVD), paged on the server. KEV ones are marked.

const PAGE = 60;
export const SEVERITY_COLOR = { critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#2563eb" };

export function CvssPill({ cvss, ui, size }) {
  const { fs } = ui;
  if (!cvss || cvss.score == null) return null;
  const c = SEVERITY_COLOR[cvss.severity] || "#71717a";
  const label = cvss.severity ? cvss.severity[0].toUpperCase() + cvss.severity.slice(1) : "";
  return (
    <span title={`CVSS base score ${cvss.score}${label ? ` (${label})` : ""}`} style={{ fontSize: fs(size === "lg" ? 11 : 10), fontWeight: 800, color: c, background: c + "18", border: `1px solid ${c}55`, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap" }}>
      {cvss.score.toFixed(1)}{label ? ` ${label}` : ""}
    </span>
  );
}

function VulnCard({ s, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const color = getConf("vulnerability").color;
  return (
    <a href={entityPath(s)} style={{ display: "flex", flexDirection: "column", gap: 7, textDecoration: "none", ...card(ui), padding: 16, minWidth: 0, ...(s.kev ? { boxShadow: "inset 3px 0 0 #dc2626" } : {}) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
        <TypeIcon type="vulnerability" color={color} size={15} />
        <span style={{ flex: "1 1 auto", fontSize: fs(15), fontWeight: 800, color: B.text, whiteSpace: "nowrap" }}>{s.name}</span>
        <CvssPill cvss={s.cvss} ui={ui} />
        <KevBadge kev={s.kev} ui={ui} />
      </div>
      {s.product && <div style={{ fontSize: fs(11), color: B.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.product}</div>}
      <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.snippet}</div>
      <div style={{ marginTop: "auto", fontSize: fs(11), color: B.textDim }}>{relationshipLine(s)}</div>
    </a>
  );
}

const sources = (days) => [
  ["", "All CVEs"], ["kev", "Exploited (CISA KEV)"], ["kevRansomware", "Exploited by ransomware"], ["notKev", "Not in KEV"],
  ["recent", `Published in the last ${days} days`], ["rules", "With detection rules"], ["attack", "Named in ATT&CK reporting"],
];
// "critical", "critical and high", "critical, high and medium"
const sevList = (list) => (list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}` : list[0] || "critical");
const SORTS = [["published", "Newest published"], ["kev", "Recently added to KEV"], ["score", "Highest CVSS"], ["rules", "Most detection rules"], ["id", "CVE ID"]];

export function KbVulnList({ meta, ui }) {
  const { B, fs, getConf } = ui;
  const cat = CATEGORY_BY_SLUG.vulnerabilities;
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [severity, setSeverity] = useState("");
  const [sort, setSort] = useState("published");
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, nvd: null });

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  // "Recently added to KEV" only makes sense for KEV CVEs.
  useEffect(() => { if (sort === "kev" && source !== "kev" && source !== "kevRansomware") setSource("kev"); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = useMemo(() => {
    const u = new URLSearchParams({ sort });
    if (query) u.set("q", query);
    if (source) u.set("source", source);
    if (severity) u.set("severity", severity);
    return `/api/kb/vulns?${u}`;
  }, [query, source, severity, sort]);

  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}&offset=0&limit=${PAGE}`)
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, nvd: d.nvd }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);

  const items = pages.flat();
  const more = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const days = state.nvd?.days || 30;
  const sev = state.nvd?.severities || ["critical"];
  const recentText = sev.length >= 5 ? `everything published in the last ${days} days` : `${sevList(sev.filter(v => v !== "none"))} CVEs published in the last ${days} days`;
  const looksLikeCve = /^cve-\d{4}-\d{4,7}$/i.test(query);

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{cat.label}</h2>
        <span style={{ fontSize: fs(13), color: getConf(cat.type).color, fontWeight: 800 }}>{meta?.counts?.vulnerabilities ? nf(meta.counts.vulnerabilities) : ""}</span>
        <StixTypeTag ui={ui} type="vulnerability" />
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 4px", maxWidth: "110ch", lineHeight: 1.6 }}>
        CVEs from the NVD ({recentText}, and every CVE CISA lists as exploited), plus any CVE named in ATT&CK reporting or detection rules. <KevBadge kev ui={ui} /> marks the ones CISA has confirmed are exploited in the wild. Any other CVE opens too: search for its ID.
      </p>
      <div style={{ fontSize: fs(11), color: B.textDim, margin: "0 0 14px" }}>{state.nvd?.builtAt ? `NVD data updated ${ago(state.nvd.builtAt)}` : state.nvd?.partial ? "Exploited CVEs loaded; recent CVEs from the NVD still loading…" : "NVD data loading…"}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by CVE ID, vendor, product or description…" aria-label="Filter vulnerabilities" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={source} onChange={e => setSource(e.target.value)} aria-label="Source" style={control(ui)}>
          {sources(days).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={severity} onChange={e => setSeverity(e.target.value)} aria-label="Severity" style={control(ui)}>
          <option value="">Any severity</option>
          {["critical", "high", "medium", "low"].map(v => <option key={v} value={v}>CVSS {v}</option>)}
          <option value="none">No CVSS score yet</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
        <StixExportLink ui={ui} cap={1000} href={`/api/kb/stix/vulns${queryOf({ q: query, source, severity })}`} />
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 10 }}>{state.loading ? "Loading…" : `${nf(state.total)} result${state.total === 1 ? "" : "s"}`}</div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{state.error}</div>}
      {!state.loading && !items.length && looksLikeCve && (
        <a href={`#/kb/e/${query.toUpperCase()}`} style={{ display: "inline-block", ...card(ui), padding: "12px 16px", fontSize: fs(13), color: B.accent, textDecoration: "none", marginBottom: 12 }}>Look up {query.toUpperCase()} in the NVD →</a>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 12 }}>
        {items.map(s => <VulnCard key={s.id} s={s} ui={ui} />)}
      </div>
      {items.length < state.total && (
        <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 14 }}>Show {Math.min(PAGE, state.total - items.length)} more</button>
      )}
    </>
  );
}
